import { AuthContext } from '@urbanx/agx.auth.client'
import {
  AustralianAddress,
  DocumentTypes as CampaignDocumentTypes,
  StaffUpdateCommissionInfoForCampaignRequestChangeType,
} from '@urbanx/agx.campaigns.client'
import {
  AgxFileUpload,
  DocumentTypes,
  UploadedDocument,
} from '@urbanx/agx-ui-components'
import { AgxSpacer } from '@urbanx/agx.ui-components2'
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
} from 'react'
import { useSelectedCampaign } from 'hooks'
import { deleteFile, getFileLink, uploadFile } from 'services'
import { AxiosProgressEvent, GenericAbortSignal } from 'axios'

export interface EvidenceUploadState {
  files: UploadedDocument[]
  isUploading: boolean
}

interface CommissionEvidenceUploadProps {
  campaignId: string
  campaignAddress?: AustralianAddress
  reasonType: StaffUpdateCommissionInfoForCampaignRequestChangeType
  uploadState: EvidenceUploadState
  setUploadState: Dispatch<SetStateAction<EvidenceUploadState>>
  required?: boolean
}

const getEvidenceFilePrefix = (
  reasonType: StaffUpdateCommissionInfoForCampaignRequestChangeType
) => {
  switch (reasonType) {
    case StaffUpdateCommissionInfoForCampaignRequestChangeType.CommissionReduction:
      return 'Commission_reduction'
    case StaffUpdateCommissionInfoForCampaignRequestChangeType.VendorChoiceConfirmed:
      return 'Vendor_choice_confirmed'
    case StaffUpdateCommissionInfoForCampaignRequestChangeType.Other:
    default:
      return 'Commission_adjustment'
  }
}

const buildAddressSegment = (address?: AustralianAddress) => {
  if (!address) return ''

  const unitNumber = address.unitNumber?.trim()
  const streetAddress = address.streetAddress?.trim() ?? ''

  return unitNumber ? `${unitNumber}_${streetAddress}` : streetAddress
}

const normalizeFileName = (value: string) =>
  value
    .replace(/\s+/g, '_')
    .replace(/[\\/]+/g, '_')
    .replace(/,/g, '')
    .replace(/[^a-zA-Z0-9_-]/g, '')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')

const CommissionEvidenceUpload = ({
  campaignId,
  campaignAddress,
  reasonType,
  uploadState,
  setUploadState,
  required = true,
}: CommissionEvidenceUploadProps) => {
  const { getAuthToken } = useContext(AuthContext)
  const { campaign } = useSelectedCampaign()

  const evidenceFileName = useMemo(() => {
    const address = campaignAddress ?? campaign?.address
    const prefix = getEvidenceFilePrefix(reasonType)
    const rawAddress = buildAddressSegment(address)
    const rawFileName = rawAddress ? `${prefix}_${rawAddress}` : prefix
    const normalizedFileName = normalizeFileName(rawFileName)
    return `${normalizedFileName}.pdf`
  }, [campaignAddress, campaign?.address, reasonType])

  const evidenceFilePath = useMemo(
    () =>
      campaignId
        ? `${campaignId}/ListingDocuments/CommissionAdjustments/${evidenceFileName}`
        : undefined,
    [campaignId, evidenceFileName]
  )

  const setIsUploading = useCallback(
    (isUploading: boolean) => {
      setUploadState((prev) =>
        prev.isUploading === isUploading ? prev : { ...prev, isUploading }
      )
    },
    [setUploadState]
  )

  const handleValueChanged = useCallback(
    ({ value }: { value?: UploadedDocument[] }) => {
      setUploadState((prev) => ({ ...prev, files: value ?? [] }))
    },
    [setUploadState]
  )

  const buildRenamedFormData = useCallback(
    (formData: FormData, fileName: string) => {
      const file = formData.get('File')
      if (!file || typeof File === 'undefined' || !(file instanceof File)) {
        return formData
      }

      const renamedFile = new File([file], fileName, {
        type: file.type,
        lastModified: file.lastModified,
      })
      const renamedFormData = new FormData()
      renamedFormData.append('File', renamedFile)
      return renamedFormData
    },
    []
  )

  const uploadEvidenceFile = useCallback(
    async (
      authToken: string,
      file: string,
      uploadCampaignId: string,
      uploadedFormType: CampaignDocumentTypes,
      uploadProgress:
        | ((progressEvent: AxiosProgressEvent) => void)
        | null = null,
      abortSignal: GenericAbortSignal | null = null
    ) => {
      setIsUploading(true)
      try {
        const formData = file as unknown as FormData
        const renamedFormData =
          typeof FormData !== 'undefined' && formData instanceof FormData
            ? buildRenamedFormData(formData, evidenceFileName)
            : formData

        return await uploadFile(
          authToken,
          renamedFormData as unknown as string,
          uploadCampaignId,
          uploadedFormType,
          uploadProgress,
          abortSignal,
          evidenceFilePath
        )
      } finally {
        setIsUploading(false)
      }
    },
    [buildRenamedFormData, evidenceFileName, evidenceFilePath, setIsUploading]
  )

  return (
    <AgxSpacer direction="vertical" gap="spacing-lg" fill>
      <AgxFileUpload
        id="commissionEvidenceUpload"
        key={`commissionEvidenceUpload-${reasonType}`}
        title="File upload"
        buttonText={uploadState.files.length ? 'Replace file' : 'Choose file'}
        fileData={uploadState.files}
        campaignId={campaignId}
        documentType={DocumentTypes.Miscellaneous}
        onValueChanged={handleValueChanged}
        getAuthToken={getAuthToken}
        getFileLink={getFileLink}
        uploadFile={uploadEvidenceFile}
        deleteFile={deleteFile}
        extensions={['pdf']}
        multiple={false}
        required={required}
      />
    </AgxSpacer>
  )
}

export default CommissionEvidenceUpload
